import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    MenuItem,
    Select
} from '@mui/material';
import DashboardCard from '../components/DashboardCard';
import {
    GoogleMap,
    Marker,
    InfoWindow
} from "@react-google-maps/api";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import Chart from 'react-apexcharts';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import { api } from 'common';
import { colors } from 'components/Theme/WebTheme';
import { FONT_FAMILY } from 'common/sharedFunctions';
// Helper function to detect airport locations
const isOHAirport = (address) => {
    if (!address) return false;
    const addr = address.toLowerCase();
    return addr.includes("o'hare") || addr.includes('ohare') || addr.includes('ord') || 
           addr.includes("o'hare international airport") || addr.includes("chicago o'hare");
};

const isMidwayAirport = (address) => {
    if (!address) return false;
    const addr = address.toLowerCase();
    return addr.includes('midway') || addr.includes('mdw') || 
           addr.includes('midway international airport') || addr.includes('chicago midway');
};

// Function to get marker color based on booking status and airport
const getMarkerColor = (booking) => {
    if (!booking) {
        return '#FFFFFF'; // White for no booking
    }
    
    const status = booking.status;
    const isStarted = status === 'STARTED' || status === 'REACHED';
    
    if (isStarted) {
        // After ride starts, check drop location
        const dropAddress = booking.drop?.add || booking.dropAddress || '';
        if (isOHAirport(dropAddress)) {
            return '#FF6B6B'; // Red for O'Hare drop
        } else if (isMidwayAirport(dropAddress)) {
            return '#4ECDC4'; // Teal for Midway drop
        }
    } else {
        // Before ride starts, check pickup location
        const pickupAddress = booking.pickup?.add || booking.pickupAddress || '';
        if (isOHAirport(pickupAddress)) {
            return '#FFD93D'; // Yellow for O'Hare pickup
        } else if (isMidwayAirport(pickupAddress)) {
            return '#6BCF7F'; // Green for Midway pickup
        }
    }
    
    return '#FFFFFF'; // Default white
};

const Dashboard = () => {
    const [mylocation, setMylocation] = useState(null);
    const [locations, setLocations] = useState([]);
    const [filteredLocations, setFilteredLocations] = useState([]);
    const [selectedDriverFilter, setSelectedDriverFilter] = useState('all');
    const [dailygross, setDailygross] = useState(0);
    const [monthlygross, setMonthlygross] = useState(0);
    const [totalgross, setTotalgross] = useState(0);

    const [settings, setSettings] = useState({});
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const usersdata = useSelector(state => state.usersdata);
    const bookinglistdata = useSelector(state => state.bookinglistdata);
    const settingsdata = useSelector(state => state.settingsdata);
    const auth = useSelector(state => state.auth);
    const cars = useSelector(state => state.cartypes.cars);
    const [allCompleteCount, setAllCompleteCount] = useState([]);
    const [allCancelCount, setAllCancelCount] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [driverCount, setDriverCount] = useState(0);
    const [customerCount, setCustomerCount] = useState(0);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {fetchDrivers, clearFetchDrivers} = api;

    useEffect(() => {
        dispatch(fetchDrivers('web'));
        return () => {
            dispatch(clearFetchDrivers());
        };
    }, [dispatch, fetchDrivers, clearFetchDrivers]);

    useEffect(() => {
        if (mylocation == null) {
            navigator.geolocation.getCurrentPosition(
                position => setMylocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                }),
                error => console.log(error)
            );
        }
    }, [mylocation]);

    useEffect(() => {
        if (settingsdata.settings) {
            setSettings(settingsdata.settings);
        }
    }, [settingsdata.settings]);

    useEffect(() => {
        if (usersdata.drivers && bookinglistdata.bookings) {
            const liveBookings = bookinglistdata.bookings.filter(bkg => bkg.status === 'STARTED');
            // Get all active bookings (ACCEPTED, ARRIVED, STARTED, REACHED)
            const activeBookings = bookinglistdata.bookings.filter(bkg => 
                bkg.status === 'ACCEPTED' || 
                bkg.status === 'ARRIVED' || 
                bkg.status === 'STARTED' || 
                bkg.status === 'REACHED'
            );
            const drivers = usersdata.drivers;
            let locs = [];
           
                for (let i = 0; i < drivers.length; i++) {
                    if (drivers[i].location) {
                        let carImage = 'https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png';
                        let bookingRef = null;
                        let currentBooking = null;
                        
                        for (let j = 0; j < cars.length; j++) {
                            if (cars[j].name === drivers[i].carType) {
                                carImage = cars[j].image;
                            }
                        }
                        
                        // Find the driver's current active booking
                        for(let j = 0; j < activeBookings.length; j++){
                            if(activeBookings[j].driver === drivers[i].uid || activeBookings[j].driver === drivers[i].id){
                                bookingRef = activeBookings[j].reference;
                                currentBooking = activeBookings[j];
                                break; // Take the first active booking
                            }
                        }
                        
                        // If no active booking found, check live bookings
                        if (!currentBooking) {
                            for(let j = 0; j < liveBookings.length; j++){
                                if(liveBookings[j].driver === drivers[i].uid || liveBookings[j].driver === drivers[i].id){
                                    bookingRef = liveBookings[j].reference;
                                    currentBooking = liveBookings[j];
                                    break;
                                }
                            }
                        }
                        
                        // Get marker color based on booking
                        const markerColor = getMarkerColor(currentBooking);
                        
                        locs.push({
                            id: i,
                            driverId: drivers[i].uid || drivers[i].id,
                            lat: drivers[i].location.lat,
                            lng: drivers[i].location.lng,
                            drivername: drivers[i].firstName + ' ' + drivers[i].lastName,
                            carnumber: drivers[i].vehicleNumber,
                            cartype: drivers[i].carType,
                            carImage: carImage,
                            bookingRef: bookingRef,
                            fleetadmin: drivers[i].fleetadmin,
                            markerColor: markerColor,
                            booking: currentBooking
                        });
                    }
                }
                if (auth.profile.usertype === 'fleetadmin') {
                    locs = locs.filter(driver => driver.fleetadmin === auth.profile.uid);
                }
                setLocations(locs);
                
        }
    }, [usersdata.drivers, auth.profile.usertype, auth.profile.uid, cars, bookinglistdata.bookings, settings]);

    // Create list of active drivers for dropdown filter
    const activeDriverOptions = useMemo(() => {
        const drivers = [];
        const uniqueDrivers = new Set();
        locations.forEach(loc => {
            const driverKey = loc.driverId || `${loc.drivername}_${loc.carnumber}`;
            if (!uniqueDrivers.has(driverKey)) {
                uniqueDrivers.add(driverKey);
                drivers.push({
                    id: driverKey,
                    name: loc.drivername,
                    carnumber: loc.carnumber,
                    displayName: `${loc.drivername} (${loc.carnumber})`
                });
            }
        });
        return drivers.sort((a, b) => a.name.localeCompare(b.name));
    }, [locations]);

    // Filter locations based on selected driver
    useEffect(() => {
        if (selectedDriverFilter === 'all') {
            setFilteredLocations(locations);
        } else {
            const filtered = locations.filter(loc => {
                const driverKey = loc.driverId || `${loc.drivername}_${loc.carnumber}`;
                return driverKey === selectedDriverFilter;
            });
            setFilteredLocations(filtered);
        }
    }, [locations, selectedDriverFilter]);
    useEffect(() => {
        if (bookinglistdata.bookings) {
            let today = new Date();
            let monthlyTotal = 0;
            let yearlyTotal = 0;
            let todayTotal = 0;
            for (let i = 0; i < bookinglistdata.bookings.length; i++) {
                if ((bookinglistdata.bookings[i].status === 'PAID' || bookinglistdata.bookings[i].status === 'COMPLETE') && ((bookinglistdata.bookings[i].fleetadmin === auth.profile.uid && auth.profile.usertype === 'fleetadmin')|| auth.profile.usertype === 'admin') ) {
                    const { tripdate, convenience_fees, fleetCommission, discount } = bookinglistdata.bookings[i];
                    let tDate = new Date(tripdate);
                    if (convenience_fees && parseFloat(convenience_fees) > 0 && auth.profile.usertype === 'admin') {
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(convenience_fees) - parseFloat(discount);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
                    }
                    if (fleetCommission && parseFloat(fleetCommission) > 0 && auth.profile.usertype === 'fleetadmin'){
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(fleetCommission);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(fleetCommission);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(fleetCommission);
                    }
                }
            }
            setDailygross(parseFloat(todayTotal).toFixed(settings.decimal));
            setMonthlygross(parseFloat(monthlyTotal).toFixed(settings.decimal));
            setTotalgross(parseFloat(yearlyTotal).toFixed(settings.decimal));
        }
    }, [bookinglistdata.bookings, settings,auth.profile.uid,auth.profile.usertype]);

    const barChartOptions = {
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        colors: [colors.GREEN, colors.RED],
        options: {
            maintainAspectRatio: false,
            scales: {
                yAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                        },
                    },
                ],
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                curve: 'smooth'
            }, 
        },
        chart: {
                height: 350,
                type: 'area',
                fontFamily: FONT_FAMILY 
            },
        xaxis: {
            labels: {
                style: {
                    fontFamily: FONT_FAMILY 
                }
            }
        },
        yaxis: {
            labels: {
                style: {
                    fontFamily: FONT_FAMILY 
                }
            }
        },
        title: {
            style: {
                fontFamily: FONT_FAMILY
            }
        },
        subtitle: {
            style: {
                fontFamily: FONT_FAMILY 
            }
        },
        tooltip: {
            style: {
                fontFamily: FONT_FAMILY 
            }
        },
        legend: {
            fontFamily: FONT_FAMILY 
        }
    };

    const barChart = [{
        name: t('completed_bookings'),
        data: allCompleteCount
    }, {
        name: t('cancelled_bookings'),
        data: allCancelCount
    }];
    useEffect(() => {
        if (usersdata.users) {
            let driverCount = 0;
            let activeCount = 0;
            let customerCount = 0;
            for (let i = 0; i < usersdata.users.length; i++) {
                if (usersdata.users[i].usertype === 'driver') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        driverCount = driverCount + 1;
                    }
                }
                if (usersdata.users[i].driverActiveStatus === true) {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        activeCount = activeCount + 1;
                    }
                }
                if (usersdata.users[i].usertype === 'customer') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        customerCount = customerCount + 1;
                    }
                }
            }
            setActiveCount(activeCount);
            setDriverCount(driverCount);
            setCustomerCount(customerCount);
        } else {
            setActiveCount(0)
            setDriverCount(0)
            setCustomerCount(0)
        }
    }, [usersdata.users, auth.profile]);

    useEffect(() => {
        let allCompleteCount = [];
        let allCancelCount = [];
        let months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        if (bookinglistdata.bookings) {
            for (let i = 0; i < months.length; i++) {
                let completeCount = 0;
                let cancelCount = 0;
                for (let j = 0; j < bookinglistdata.bookings.length; j++) {
                    const { tripdate } = bookinglistdata.bookings[j];
                    let tDate = new Date(tripdate);
                    if (months[i] === months[tDate.getMonth()] && (bookinglistdata.bookings[j].status === 'PAID' || bookinglistdata.bookings[j].status === 'COMPLETE')) {
                        completeCount = completeCount + 1;
                    }
                    if (months[i] === months[tDate.getMonth()] && bookinglistdata.bookings[j].status === 'CANCELLED') {
                        cancelCount = cancelCount + 1;
                    }
                }
                allCompleteCount.push(completeCount);
                allCancelCount.push(cancelCount);
            }
        }
        setAllCompleteCount(allCompleteCount);
        setAllCancelCount(allCancelCount);
    }, [bookinglistdata.bookings]);

    return (
        bookinglistdata.loading || usersdata.loading ? <CircularLoading /> :
            <div >
                <Typography variant="h4" style={{ margin: "20px 20px 0 15px", textAlign: isRTL === 'rtl' ? 'right' : 'left',fontFamily: FONT_FAMILY }}>{t('gross_earning')}</Typography>
                <Grid container direction="row" spacing={2}>
                    <Grid item xs={12} sm={12} md={3} lg={3} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left', }}>
                        {settings.swipe_symbol === false ?
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.WALLET_CARD,color:colors.WHITE }} title={t('today_text')} image={require("../assets/img/money1.jpg").default}>{settings.symbol + ' ' + dailygross}</DashboardCard>
                            :
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.WALLET_CARD,color:colors.WHITE }} title={t('today_text')} image={require("../assets/img/money1.jpg").default}>{dailygross + ' ' + settings.symbol}</DashboardCard>
                        }
                    </Grid>
                    <Grid item xs={12} sm={12} md={3} lg={3} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
                        {settings.swipe_symbol === false ?
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.VIOLET,color:colors.WHITE }} title={t('this_month_text')} image={require("../assets/img/money2.jpg").default}>{settings.symbol + ' ' + monthlygross}</DashboardCard>
                            :
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.VIOLET,color:colors.WHITE }} title={t('this_month_text')} image={require("../assets/img/money2.jpg").default}>{monthlygross + ' ' + settings.symbol}</DashboardCard>
                        }
                    </Grid>
                    <Grid item xs={12} sm={12} md={3} lg={3} style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left' }}>
                        {settings.swipe_symbol === false ?
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.GREEN,color:colors.WHITE }} title={t('total')} image={require("../assets/img/money3.jpg").default}>{settings.symbol + ' ' + totalgross}</DashboardCard>
                            :
                            <DashboardCard crdStyle={{ display: 'flex', borderRadius: "19px", backgroundColor: colors.GREEN,color:colors.WHITE }} title={t('total')} image={require("../assets/img/money3.jpg").default}>{totalgross + ' ' + settings.symbol}</DashboardCard>
                        }
                    </Grid>
                </Grid>
                
                <Grid item xs={12} style={{ marginTop: 20 }}>
                    <Grid container style={{  direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }} >
                        <Grid item xs={12} sm={12} md={9} lg={9} style={{ backgroundColor: colors.WHITE, borderRadius: 20, overflow: 'hidden', }} boxShadow={3}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px' }}>
                                <Typography variant="h4" style={{ textAlign: isRTL === 'rtl' ? 'right' : 'left', fontFamily: FONT_FAMILY }}>{t('real_time_driver_section_text')}</Typography>
                                <FormControl 
                                    sx={{ 
                                        minWidth: 250,
                                        maxWidth: 400,
                                        backgroundColor: colors.WHITE,
                                    }}
                                >
                                    <InputLabel 
                                        id="driver-filter-label"
                                        style={{ fontFamily: FONT_FAMILY }}
                                    >
                                        {t('select_driver')}
                                    </InputLabel>
                                    <Select
                                        labelId="driver-filter-label"
                                        id="driver-filter-select"
                                        value={selectedDriverFilter}
                                        label={t('select_driver')}
                                        onChange={(e) => setSelectedDriverFilter(e.target.value)}
                                        style={{ fontFamily: FONT_FAMILY }}
                                    >
                                        <MenuItem value="all" style={{ fontFamily: FONT_FAMILY }}>
                                            {t('All')}
                                        </MenuItem>
                                        {activeDriverOptions.map((driver) => (
                                            <MenuItem 
                                                key={driver.id} 
                                                value={driver.id}
                                                style={{ fontFamily: FONT_FAMILY }}
                                            >
                                                {driver.displayName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            {mylocation && mylocation.lat?
                            <GoogleMap
                                zoom={10}
                                center={mylocation}
                                mapContainerStyle={{height: `500px`,fontFamily: FONT_FAMILY }}
                                >
                                {filteredLocations.map(marker => {
                                    // Create a colored circle icon for the marker
                                    const iconColor = marker.markerColor || '#FFFFFF';
                                    const isWhite = iconColor === '#FFFFFF';
                                    
                                    // Create SVG icon with colored circle background
                                    // Use a simple colored circle since external images in SVG don't work well in Google Maps
                                    const svgIcon = `
                                        <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
                                            <circle cx="25" cy="25" r="22" fill="${iconColor}" stroke="${isWhite ? '#000000' : iconColor}" stroke-width="3" opacity="0.8"/>
                                            <circle cx="25" cy="25" r="15" fill="white" opacity="0.3"/>
                                        </svg>
                                    `;
                                    
                                    const iconUrl = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgIcon);
                                    
                                    return (
                                        <Marker
                                            position={{ lat: marker.lat, lng: marker.lng }}
                                            key={marker.id}
                                            icon={{
                                                url: iconUrl,
                                                scaledSize: new window.google.maps.Size(50, 50),
                                                anchor: new window.google.maps.Point(25, 25)
                                            }}
                                        >
                                            <InfoWindow 
                                               position={{ lat: marker.lat, lng: marker.lng }} 
                                               options={{ pixelOffset: new window.google.maps.Size(0, -32) }}
                                            >
                                                <div style={{ fontFamily: FONT_FAMILY }}>
                                                    {marker.drivername}<br/>
                                                    {marker.carnumber}
                                                    {marker.bookingRef && <><br/>Booking: {marker.bookingRef}</>}
                                                    {marker.booking && (
                                                        <>
                                                            <br/>Status: {marker.booking.status}
                                                            {marker.booking.pickup?.add && <><br/>Pickup: {marker.booking.pickup.add.substring(0, 30)}...</>}
                                                            {marker.booking.drop?.add && <><br/>Drop: {marker.booking.drop.add.substring(0, 30)}...</>}
                                                        </>
                                                    )}
                                                </div>
                                            </InfoWindow>
                                        </Marker>
                                    );
                                })}
                            </GoogleMap>
                            :null}
                        </Grid>
                        <Grid item xs={12} sm={12} md={3} lg={3} style={{ padding: 25, marginTop: '30px' }}>
                          
                            <Card 
                                onClick={() => navigate('/users/0')}
                                style={{ 
                                    borderRadius: "19px", 
                                    backgroundColor: colors.WALLET_CARD, 
                                    minHeight: 100, 
                                    marginBottom: 20,
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s, box-shadow 0.2s'
                                }}
                                sx={{
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                    }
                                }}
                            >
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 20, marginTop: 20,fontFamily: FONT_FAMILY }}>{t('total_cumtomer')}</Typography>
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 26,fontWeight:'bold',fontFamily: FONT_FAMILY}}>{customerCount}</Typography>
                            </Card>
                            <Card 
                                onClick={() => navigate('/users/1')}
                                style={{ 
                                    borderRadius: "19px", 
                                    backgroundColor: colors.VIOLET, 
                                    minHeight: 100, 
                                    marginBottom: 20,
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s, box-shadow 0.2s'
                                }}
                                sx={{
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                    }
                                }}
                            >
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 20, marginTop: 20,fontFamily: FONT_FAMILY }}>{t('total_drivers')}</Typography>
                                <Typography style={{ color: 'white', textAlign: 'center',  fontSize: 26,fontWeight:'bold',fontFamily: FONT_FAMILY}}>{driverCount}</Typography>
                            </Card>
                            <Card 
                                onClick={() => navigate('/assignedride?tab=1')}
                                style={{ 
                                    borderRadius: "19px", 
                                    backgroundColor: colors.GREEN, 
                                    minHeight: 100,
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s, box-shadow 0.2s'
                                }}
                                sx={{
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                    }
                                }}
                            >
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 20, marginTop: 20 ,fontFamily: FONT_FAMILY}}>{t('active_driver')}</Typography>
                                <Typography style={{ color: 'white', textAlign: 'center',  fontSize: 26,fontWeight:'bold' ,fontFamily: FONT_FAMILY}}>{activeCount}</Typography>
                            </Card>
                            <Card 
                                onClick={() => navigate('/bookings?tab=today')}
                                style={{ 
                                    borderRadius: "19px", 
                                    backgroundColor: colors.BLUE, 
                                    minHeight: 100, 
                                    marginBottom: 20,
                                    marginTop:20,
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s, box-shadow 0.2s'
                                }}
                                sx={{
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                    }
                                }}
                            >
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 20, marginTop: 20, fontFamily: FONT_FAMILY }}>{t('active_booking')}</Typography>
                                <Typography style={{ color: 'white', textAlign: 'center', fontSize: 26, fontWeight: 'bold', fontFamily: FONT_FAMILY }}>{bookinglistdata.bookings ? bookinglistdata.bookings.filter(bkg => bkg.status === 'STARTED').length : 0}</Typography>
                            </Card>

                        </Grid>

                    </Grid>
                </Grid>
                <Box style={{ padding: 10, marginTop: 20, borderRadius: 20, backgroundColor: colors.WHITE}} boxShadow={6}>
                    <Typography style={{margin: "5px 20px 0 15px",color:colors.GREEN, fontWeight:"bold",fontFamily: FONT_FAMILY}} variant="h5">{t('booking_chart')}</Typography>
                    <Chart
                        options={barChartOptions}
                        series={barChart}
                        type="area"
                        width="100%"
                        height={360}
                    />
                </Box>
            </div>

    )
}

export default Dashboard;